/**
 * Accepting a partner portal invitation. The link's token is taken out of the address bar at
 * once and kept only in this tab until it is accepted or declined, so a failed request can be
 * retried and the token isn't left in history. The person sees exactly what is shared and
 * accepts explicitly. "Try again" (no answer, throttled, server error) is kept apart from an
 * invitation that is expired, revoked or for another email.
 */
import { useCallback, useEffect, useState } from 'react';
import { acceptPortalInvitation, inviteFailure, previewPortalInvitation, type PortalInvitePreview } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n, type MessageKey } from '../lib/i18n.tsx';
import { Banner, Button, Spinner } from '../ui/ui.tsx';
import { Wordmark } from '../ui/icons.tsx';

const KEY = 'miraops.portalToken';

/** Moves ?portal_token=… from the address bar into this tab's storage. */
export function takePortalToken(): string | null {
  const q = new URLSearchParams(window.location.search);
  const fromUrl = q.get('portal_token');
  if (fromUrl) {
    q.delete('portal_token');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    try { sessionStorage.setItem(KEY, fromUrl); } catch { /* kept in memory only */ }
    return fromUrl;
  }
  try { return sessionStorage.getItem(KEY); } catch { /* ignored: browser storage blocked: no link kept from earlier in this tab */  return null; }
}
export function forgetPortalToken() { try { sessionStorage.removeItem(KEY); } catch { /* nothing stored */ } }

type View = { kind: 'loading' } | { kind: 'preview'; preview: PortalInvitePreview } | { kind: 'retry' } | { kind: 'accepting' };

export function PortalInvite({ token, email, onAccepted, onDone, onSignOut }: {
  token: string; email: string; onAccepted: (invitationId: string) => void; onDone: () => void; onSignOut: () => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [failure, setFailure] = useState<string | null>(null);
  const load = useCallback(async () => {
    setView({ kind: 'loading' }); setFailure(null);
    try { setView({ kind: 'preview', preview: await previewPortalInvitation(transport, token) }); }
    catch (error) { setView(inviteFailure(error) === 'retry' ? { kind: 'retry' } : { kind: 'preview', preview: { state: 'invalid', workspace_name: null, company_name: null, orders: null, files: null, expires_at: null } }); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  async function accept(preview: PortalInvitePreview) {
    setView({ kind: 'accepting' }); setFailure(null);
    try {
      // Accepting twice is harmless: a lost answer is retried with the same token.
      const r = await acceptPortalInvitation(transport, token);
      forgetPortalToken();
      onAccepted(r.invitation_id);
    } catch (error) {
      const f = inviteFailure(error);
      if (f === 'retry') { setFailure(t('invite.noAnswer')); setView({ kind: 'preview', preview }); }
      else { setView({ kind: 'preview', preview: { ...preview, state: f === 'other_email' ? 'other_email' : 'invalid' } }); }
    }
  }
  const decline = () => { forgetPortalToken(); onDone(); };

  const p = view.kind === 'preview' ? view.preview : null;
  return (
    <main className="mcpauth" aria-labelledby="invite-title">
      <div className="mcpauth-card">
        <header className="mcpauth-head"><Wordmark /><h1 id="invite-title">{t('invite.title')}</h1></header>
        <div className="mcpauth-body">
          {view.kind === 'loading' || view.kind === 'accepting' ? <div className="center pad"><Spinner label={t('state.loading')} /></div>
          : view.kind === 'retry' ? <>
            <Banner tone="attention">{t('invite.noAnswer')}</Banner>
            <div className="row-actions start"><Button onClick={decline}>{t('invite.later')}</Button><Button variant="primary" onClick={() => void load()}>{t('state.retry')}</Button></div>
          </> : p && p.state === 'other_email' ? <>
            <Banner tone="attention">{t('invite.otherEmail', { email })}</Banner>
            <div className="row-actions start"><Button onClick={decline}>{t('invite.dismiss')}</Button><Button variant="primary" onClick={onSignOut}>{t('invite.switchAccount')}</Button></div>
          </> : p && p.state !== 'ready' && p.state !== 'accepted' ? <>
            <Banner tone="danger">{t(`invite.state.${p.state}` as MessageKey)}</Banner>
            <p className="muted">{t('invite.askAgain')}</p>
            <div className="row-actions start"><Button variant="primary" onClick={decline}>{t('invite.dismiss')}</Button></div>
          </> : p && <>
            <p>{t('invite.from', { workspace: p.workspace_name ?? '—', company: p.company_name ?? '—' })}</p>
            <dl className="kv">
              <dt>{t('invite.shipments')}</dt><dd>{p.orders ?? 0}</dd>
              <dt>{t('invite.files')}</dt><dd>{p.files ?? 0}</dd>
              <dt>{t('invite.until')}</dt><dd>{p.expires_at ? i18n.date.format(Date.parse(p.expires_at)) : '—'}</dd>
              <dt>{t('invite.account')}</dt><dd>{email}</dd>
            </dl>
            <p className="muted small">{t('invite.whatYouSee')}</p>
            {failure && <Banner tone="attention">{failure}</Banner>}
            <div className="row-actions start">
              <Button onClick={decline}>{t('invite.notNow')}</Button>
              <Button variant="primary" onClick={() => void accept(p)}>{t(p.state === 'accepted' ? 'invite.open' : 'invite.accept')}</Button>
            </div>
          </>}
        </div>
      </div>
    </main>
  );
}
